import sortBy from 'lodash/sortBy'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { dependsOn, getError, getValue, isPending, trackedAction } from 'mobx-async'

import {
  BookingPeriodReference,
  BookingSorting,
  BookingTier,
  LeadSource,
  RiskLevel,
  SortOrder,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { Alert, HASH_CODE_REGEX } from '@avantstay/backoffice-core'

import { getBookingWithTags } from '@new/__global/components/SidePanelRenderer/SidePanels/AddBookingTagsSidePanel/stores/api/tagsArriere'
import type {
  AlterAirbnbBookingProps,
  PreviewPetCountAlterationQuoteVariables,
} from '@new/domains/bookings/_types'
import { IContractsDataProps } from '@new/domains/bookings/screens/bookingAgreements/BookingAgreements.types'
import { AlterBookingDetailsMutation } from '@new/domains/bookings/screens/bookingInfo/BookingInfo.types'
import { BookingsTapeChartStore } from '@new/domains/bookingsTapeChart/stores/BookingsTapeChartStore'

import { BookingView, ReservationType } from '@/types/servicesTypes/Bookings.types'
import { arriere } from '@arriere/index'
import { bookingModificationHistory, getBookingHistory } from '@arriere/modules/bookingHistory'
import {
  acceptGuestAirbnbAlteration,
  alterAirbnbBooking,
  bookingModificationPossibility,
  cancelHostAirbnbAlteration,
  declineGuestAirbnbAlteration,
  getBookingAccessMethods,
  getBookingBlendedDiscounts,
  getBookingInfoById,
  getPendingAirbnbAlterations,
  getPendingAlterationRelocationHome,
  previewAirbnbPetCountAlterationQuote,
  previewPetCountAlterationQuote,
} from '@arriere/modules/bookings'
import { getCoupon } from '@arriere/modules/coupons'
import { PendingAlterationRelocationHome } from '@arriere/types/BookingInfo.types'
import { ROUTES } from '@routes'
import { computedRouteParam } from '@stores/computedRouteParam'
import { trackError, trackEvent } from '@tracking/customEventReport'

import { AlterBookingInput, RenamedMainGuest } from './_types'

class _BookingStore {
  private bookingCacheObject?: BookingView = undefined

  private renamedMainGuest?: RenamedMainGuest = undefined

  riskLevel?: RiskLevel

  leadSource?: LeadSource

  periodReference?: BookingPeriodReference

  tier?: BookingTier

  sortBy?: BookingSorting

  sortOrder?: SortOrder

  minPrice?: number

  longTerm?: boolean

  currentBookingSection?: string = undefined

  reservationTypeOptions?: ReservationType

  pendingAlterationRelocationHome?: PendingAlterationRelocationHome

  forceUpdateBookingInfo = 0

  forceUpdateBookingBlendedDiscounts = 0

  forceUpdateBookingAccessMethods = 0

  private bookingHashParam = computedRouteParam('hash', {
    patterns: [
      ROUTES.booking.path,
      ROUTES.bookingInfo.path,
      ROUTES.bookingGuests.path,
      ROUTES.bookingCases.path,
      ROUTES.bookingGuestDamage.path,
      ROUTES.bookingAgreements.path,
      ROUTES.bookingPaymentDetails.path,
      ROUTES.bookingPaymentAndServices.path,
      ROUTES.bookingValueAddedServices.path,
      ROUTES.bookingAccountingTimeline.path,
      ROUTES.bookingMessages.path,
      ROUTES.bookingReviews.path,
      ROUTES.bookingHistory.path,
      ROUTES.bookingUserSessions.path,
      ROUTES.fastTapeChart.path,
      ROUTES.bookingSMSConversations.path,
      ROUTES.bookingTimeline.path,
    ],
  })

  private bookingIdParam = computedRouteParam('booking-id', {
    patterns: [ROUTES.ratingsReviewsManagement.path],
  })

  constructor() {
    makeObservable<_BookingStore, 'bookingCacheObject'>(this, {
      bookingCacheObject: observable,
      riskLevel: observable,
      leadSource: observable,
      periodReference: observable,
      tier: observable,
      sortBy: observable,
      sortOrder: observable,
      minPrice: observable,
      longTerm: observable,
      currentBookingSection: observable,
      reservationTypeOptions: observable,
      pendingAlterationRelocationHome: observable,
      forceUpdateBookingInfo: observable,
      forceUpdateBookingBlendedDiscounts: observable,
      forceUpdateBookingAccessMethods: observable,
      bookingHash: computed,
      bookingId: computed,
      bookingCache: computed,
      bookingIdFromTapeChart: computed,
      bookingDataFromServer: computed,
      bookingBlendedDiscounts: computed,
      isLoadingBookingBlendedDiscounts: computed,
      errorBookingBlendedDiscounts: computed,
      bookingBlendedDiscountsDataFromServer: computed,
      setForceUpdateBookingBlendedDiscounts: action.bound,
      booking: computed,
      bookingInfo: computed,
      isLoadingBookingInfo: computed,
      contracts: computed,
      bookingLockCodes: computed,
      bookingHistory: computed,
      bookingAirbnbHistory: computed,
      bookingCoupon: computed,
      reservationTypes: computed,
      bookingModificationPossibility: computed,
      pendingAirbnbAlteration: computed,
      setBookingHashParam: action.bound,
      setBookingIdParam: action.bound,
      setBookingCache: action.bound,
      setRenamedMainGuest: action.bound,
      setForceUpdateBookingInfo: action.bound,
      setForceUpdateBookingAccessMethods: action.bound,
      refetchTagsOnly: action.bound,
      updateBookingValuesFromServer: action.bound,
    })

    this.alterBookingMainGuest = trackedAction<any>(this.alterBookingMainGuest.bind(this))
    this.cancelHostAirbnbAlterationRequest = trackedAction<any>(
      this.cancelHostAirbnbAlterationRequest.bind(this),
    )
    this.declineGuestAirbnbAlterationRequest = trackedAction<any>(
      this.declineGuestAirbnbAlterationRequest.bind(this),
    )
    this.acceptGuestAirbnbAlterationRequest = trackedAction<any>(
      this.acceptGuestAirbnbAlterationRequest.bind(this),
    )
    this.alterAirbnbBooking = trackedAction<any>(this.alterAirbnbBooking.bind(this))
    this.alterBooking = trackedAction<any>(this.alterBooking.bind(this))
    this.getAlterationRelocationHome = trackedAction<any>(
      this.getAlterationRelocationHome.bind(this),
    )
    this.getAirbnbPetCountAlterationQuote = trackedAction<any>(
      this.getAirbnbPetCountAlterationQuote.bind(this),
    )
    this.getPetCountAlterationQuote = trackedAction<any>(this.getPetCountAlterationQuote.bind(this))
    this.alterBookingDetails = trackedAction<any>(this.alterBookingDetails.bind(this))
    this.cancelRentalAgreement = trackedAction<any>(this.cancelRentalAgreement.bind(this))
    this.resendRentalAgreement = trackedAction<any>(this.resendRentalAgreement.bind(this))
    this.createRentalAgreement = trackedAction<any>(this.createRentalAgreement.bind(this))
  }

  get bookingHash() {
    return this.bookingHashParam.get()
  }

  get bookingId() {
    return this.bookingIdParam.get()
  }

  isCouponInfoOpen = computedRouteParam<boolean | undefined>('is-coupon-info-open', {
    patterns: [ROUTES.bookingPaymentAndServices.path],
    parse: JSON.parse,
  })

  get bookingCache() {
    return this.bookingCacheObject
  }

  get bookingIdFromTapeChart() {
    return BookingsTapeChartStore.bookingIdParam.get()
  }

  get bookingDataFromServer() {
    void [this.forceUpdateBookingInfo]

    const hash = this.bookingHash
    const bookingId = this.bookingIdFromTapeChart ?? this.bookingId

    if (hash && HASH_CODE_REGEX.test(hash)) {
      return getBookingInfoById({ hash, alias: 'getBookingInfoByHash' })
    }

    if (!bookingId) return Promise.resolve(undefined)

    return getBookingInfoById({ bookingId })
  }

  get bookingBlendedDiscounts() {
    return getValue(this.bookingBlendedDiscountsDataFromServer) ?? []
  }

  get isLoadingBookingBlendedDiscounts() {
    return isPending(this.bookingBlendedDiscountsDataFromServer)
  }

  get errorBookingBlendedDiscounts() {
    return getError(this.bookingBlendedDiscountsDataFromServer)
  }

  get bookingBlendedDiscountsDataFromServer() {
    void [this.forceUpdateBookingBlendedDiscounts]

    const bookingId = this.bookingInfo?.id
    if (!bookingId) {
      return Promise.resolve([])
    }
    return getBookingBlendedDiscounts(bookingId)
  }

  setForceUpdateBookingBlendedDiscounts() {
    this.forceUpdateBookingBlendedDiscounts += 1
  }

  get booking() {
    // Computation dependencies
    return this.bookingDataFromServer.then(data => {
      const booking = data ? this.withRenamedMainGuest(data) : data

      // Only cache a real booking. A refetch resolves undefined before the new data lands, and
      // caching that emptied the header mid-session — unmounting everything under it, including
      // open modals, with no close event. Consumers still stop showing a stale booking because
      // they compare the cached hash against the current one.
      if (booking) {
        this.setBookingCache(booking)
      }

      return booking
    })
  }

  get bookingInfo() {
    return getValue(this.booking)
  }

  get isLoadingBookingInfo() {
    return isPending(this.bookingDataFromServer)
  }

  get contracts(): Promise<IContractsDataProps | undefined> {
    dependsOn(this.createRentalAgreement, this.cancelRentalAgreement, this.resendRentalAgreement)

    const bookingId = this.bookingInfo?.id
    if (bookingId) {
      return arriere.bookings.bookingContracts(bookingId).then(results => {
        const sortedContracts = sortBy(results.contracts, 'createdAt').reverse()
        return {
          ...results,
          contracts: sortedContracts,
        }
      })
    }

    return Promise.resolve(undefined)
  }

  get bookingLockCodes() {
    void [this.forceUpdateBookingAccessMethods]

    const bookingId = this.bookingInfo?.id

    if (bookingId) {
      return getBookingAccessMethods(bookingId)
    }
    return Promise.resolve(undefined)
  }

  setForceUpdateBookingAccessMethods() {
    this.forceUpdateBookingAccessMethods += 1
  }

  get bookingHistory() {
    // Any action that refreshes the booking also writes a history entry, so the timeline follows the
    // same counter. The computed is lazy, so this only refetches while the history is on screen.
    void [this.forceUpdateBookingInfo]

    const bookingId = this.bookingInfo?.id

    if (bookingId) {
      return getBookingHistory(bookingId)
    }
    return Promise.resolve(undefined)
  }

  get bookingAirbnbHistory() {
    const bookingId = this.bookingInfo?.id

    if (bookingId) {
      return bookingModificationHistory(bookingId)
    }
    return Promise.resolve(undefined)
  }

  get bookingCoupon() {
    const couponCode = this.bookingInfo?.couponCode

    if (couponCode) {
      return getCoupon(couponCode)
    }

    return Promise.resolve(undefined)
  }

  get reservationTypes() {
    if (!this.reservationTypeOptions?.length) {
      return arriere.bookings.reservationTypes().then(data => {
        this.reservationTypeOptions = data

        return data
      })
    }

    return Promise.resolve(undefined)
  }

  get bookingModificationPossibility() {
    const bookingId = this.bookingInfo?.id

    if (bookingId) {
      return bookingModificationPossibility(bookingId)
    }
    return Promise.resolve(undefined)
  }

  get pendingAirbnbAlteration() {
    dependsOn(
      this.alterAirbnbBooking,
      this.acceptGuestAirbnbAlterationRequest,
      this.cancelHostAirbnbAlterationRequest,
      this.declineGuestAirbnbAlterationRequest,
    )

    const bookingInfo = this.bookingInfo

    if (!bookingInfo) {
      return Promise.resolve(undefined)
    }

    if (bookingInfo.source === LeadSource.airBnb && bookingInfo.otaBookingInfo?.otaBookingId) {
      return getPendingAirbnbAlterations({
        bookingId: bookingInfo.id,
        confirmationCode: bookingInfo.otaBookingInfo.otaBookingId,
        homeId: bookingInfo.homeId,
      })
        .then(async response => {
          if (response?.metadata?.changes?.listingId?.to) {
            await this.getAlterationRelocationHome({
              homeId: response.payload?.homeId,
              isFromHotel: bookingInfo.isFromHotel,
            })
          }
          return response
        })
        .catch(error => {
          Alert.Error(
            'An error occurred when getting pending airbnb alteration. Please try again..',
          )
          trackError(
            'Booking Airbnb Alteration: An error occurred when getting airbnb alteration info.',
            { error },
          )
          return undefined
        })
    }

    return Promise.resolve(undefined)
  }

  setBookingHashParam(bookingHash: string) {
    this.bookingHashParam.replace(bookingHash)
  }

  setBookingIdParam(bookingId?: string) {
    this.bookingIdParam.replace(bookingId)
  }

  setBookingCache(booking?: BookingView) {
    this.bookingCacheObject = booking
  }

  setRenamedMainGuest(renamedMainGuest: RenamedMainGuest) {
    this.renamedMainGuest = renamedMainGuest

    if (this.bookingCacheObject?.guestId === renamedMainGuest.guestId) {
      this.bookingCacheObject = { ...this.bookingCacheObject, guestName: renamedMainGuest.fullName }
    }
  }

  private withRenamedMainGuest(booking: BookingView): BookingView {
    const renamedMainGuest = this.renamedMainGuest
    if (booking.guestId !== renamedMainGuest?.guestId) return booking

    if (booking.guestName === renamedMainGuest.fullName) {
      this.renamedMainGuest = undefined
      return booking
    }

    return { ...booking, guestName: renamedMainGuest.fullName }
  }

  setForceUpdateBookingInfo() {
    this.forceUpdateBookingInfo += 1
  }

  async refetchTagsOnly(): Promise<void> {
    const bookingId = this.bookingInfo?.id
    if (!bookingId) return

    try {
      const tagsData = await getBookingWithTags(bookingId)

      runInAction(() => {
        if (this.bookingCacheObject && tagsData?.tags?.bookingTags) {
          this.bookingCacheObject = {
            ...this.bookingCacheObject,
            tags: {
              ...this.bookingCacheObject.tags,
              bookingTags: tagsData.tags.bookingTags,
            },
          }
        }
      })
    } catch (error) {
      console.error('Failed to refetch tags:', error)
      throw error
    }
  }

  async updateBookingValuesFromServer() {
    this.setForceUpdateBookingInfo()
  }

  async createRentalAgreement(bookingId: string) {
    try {
      await arriere.bookings.createRentalAgreement(bookingId).then(
        async response => {
          await this.updateBookingValuesFromServer()
          if (response) {
            Alert.Success('Guest agreement was sent!')
          } else {
            Alert.Error('An error occurred when sending the contract.')
          }
        },
        error => {
          Alert.Error(`Oops! Something went wrong: ${error}.`)
        },
      )
    } catch (error) {
      Alert.Error(`Oops! We were not able to process your request.`)
    }
  }

  async resendRentalAgreement(contractId: string): Promise<void> {
    try {
      await arriere.bookings.resendRentalAgreement(contractId).then(
        response => {
          if (response) {
            Alert.Success('Guest agreement was sent again!')
          } else {
            Alert.Error('An error occurred when sending the contract.')
          }
        },
        error => {
          Alert.Error(`Oops! Something went wrong: ${error}.`)
        },
      )
    } catch (error) {
      Alert.Error(`Oops! We were not able to process your request.`)
    }
  }

  async cancelRentalAgreement(contractId: string): Promise<void> {
    try {
      await arriere.bookings.cancelRentalAgreement(contractId).then(
        async response => {
          if (response) {
            await this.updateBookingValuesFromServer()
            Alert.Success('Agreement successfully removed.')
          } else {
            Alert.Error('We were not able to delete the agreement. Please try again.')
          }
        },
        error => {
          Alert.Error(`Oops! Something went wrong: ${error}.`)
        },
      )
    } catch (error) {
      Alert.Error(`Oops! We were not able to process your request.`)
    }
  }

  async alterBookingDetails(values: AlterBookingDetailsMutation) {
    const { errors, data } = await arriere.bookings.alterDetails(values)

    trackEvent('Booking Alter details: Attempting to alter booking details.', { values })

    if (errors) {
      Alert.Error('An error occurred when updating booking info. Please try again..')
      trackError('Booking Alter details: An error occurred when saving booking info.', { values })
      return
    }

    if (data.alterDetails) {
      Alert.Success('Booking details updated successfully!')
      trackEvent('Booking Alter details: Booking details updated successfully.', { values })
      this.updateBookingValuesFromServer()
    }
  }

  async getPetCountAlterationQuote({ bookingId, pets }: PreviewPetCountAlterationQuoteVariables) {
    return await previewPetCountAlterationQuote({ bookingId, pets })
      .then(
        response => {
          if (response) {
            return response.data.previewAlteration
          } else {
            Alert.Error('An error occurred when getting new pet fee price')
            trackError('Error when previewing pet count alteration', { bookingId, pets })
          }
        },
        error => {
          Alert.Error(`Oops! Something went wrong: ${error}.`)
          trackError('Error when previewing pet count alteration', { bookingId, pets })
        },
      )
      .catch(() => {
        Alert.Error(`An error occurred when getting new pet fee price`)
        trackError('Error when previewing pet count alteration', { bookingId, pets })
      })
  }

  async getAirbnbPetCountAlterationQuote({
    bookingId,
    pets,
  }: PreviewPetCountAlterationQuoteVariables) {
    return await previewAirbnbPetCountAlterationQuote({ bookingId, pets })
      .then(
        response => {
          if (response.data) {
            return response.data.previewAlteration
          }

          if (response.errors && response.errors.length > 0) {
            const errorMessage = response.errors?.flatMap(error => error?.message).join('\n')
            trackError('Error when previewing pet count alteration', {
              id: bookingId,
              error: errorMessage,
            })
            Alert.Error(
              `An error occurred when getting new pet fee price. Details: ${errorMessage}`,
            )
            return
          }

          Alert.Error('An error occurred when getting new pet fee price')
          trackError('Error when previewing pet count alteration', { bookingId, pets })
        },
        error => {
          Alert.Error(`Oops! Something went wrong: ${error}.`)
          trackError('Error when previewing pet count alteration', { bookingId, pets })
        },
      )
      .catch(() => {
        Alert.Error(`An error occurred when getting new pet fee price`)
        trackError('Error when previewing pet count alteration', { bookingId, pets })
      })
  }

  async getAlterationRelocationHome({
    homeId,
    isFromHotel,
  }: {
    homeId?: string
    isFromHotel?: boolean
  }) {
    if (homeId) {
      return await getPendingAlterationRelocationHome({ homeId, isFromHotel })
        .then(
          relocationHome => {
            if (relocationHome) {
              this.pendingAlterationRelocationHome = relocationHome
              return relocationHome
            } else {
              this.pendingAlterationRelocationHome = undefined
              Alert.Error('An error occurred when getting relocation home info')
              trackError('Error when getting relocation home info', { homeId })
            }
          },
          error => {
            this.pendingAlterationRelocationHome = undefined
            Alert.Error(`Oops! Something went wrong: ${error}.`)
            trackError('Error when getting relocation home info', { homeId })
          },
        )
        .catch(() => {
          this.pendingAlterationRelocationHome = undefined
          Alert.Error(`An error occurred when getting relocation home info`)
          trackError('Error when getting relocation home info', { homeId })
        })
    }

    this.pendingAlterationRelocationHome = undefined
    return Promise.resolve(undefined)
  }

  async alterBooking({ bookingId, newFees, nightPrices }: AlterBookingInput) {
    try {
      if (!bookingId || !nightPrices) throw new Error('Booking id or night prices was not provided')
      const result = await arriere.bookings.alterBooking({
        bookingId,
        newFees,
        nightPrices,
      })

      if (result.warnings && result.warnings.length > 0) {
        trackError('Booking Modify Warning', { id: bookingId, warning: result.warnings[0].message })
        Alert.Error(
          `Something went wrong, please review your information and try again. Details: ${result.warnings[0].message}`,
        )
        return false
      }

      if (result.errors && result.errors.length > 0) {
        const errorMessage = result.errors?.flatMap(error => error?.message).join('\n')
        trackError('Booking Modify Error', { id: bookingId, error: errorMessage })
        Alert.Error(`Something went wrong, please try again. Details: ${errorMessage}`)
        return false
      }

      if (result.data) {
        trackEvent('Booking Modify Success', { bookingId, newFees })
        Alert.Success('Booking modified with success!')
        await this.updateBookingValuesFromServer()
        return
      } else {
        throw new Error('Booking modification failed')
      }
    } catch (error) {
      Alert.Error(`An error occurred when submitting the booking alteration`)
      trackError('Error when submitting the booking alteration', {
        bookingId,
        newFees,
        nightPrices,
      })
    }
  }

  async alterAirbnbBooking({ bookingId, newNumberOfPets }: AlterAirbnbBookingProps) {
    try {
      if (!bookingId) throw new Error('Booking id was not provided')
      Alert.Info(
        "We're processing your alteration request. Please wait while Airbnb confirms the changes.",
      )
      const result = await alterAirbnbBooking({
        bookingId,
        newNumberOfPets,
      })

      if (result.warnings && result.warnings.length > 0) {
        trackError('Booking Modify Warning', {
          id: bookingId,
          warning: result.warnings[0].message,
        })
        Alert.Error(
          `Something went wrong, please review your information and try again. Details: ${result.warnings[0].message}`,
        )
        return false
      }

      if (result.errors && result.errors.length > 0) {
        const errorMessage = result.errors?.flatMap(error => error?.message).join('\n')
        trackError('Booking Modify Error', { id: bookingId, error: errorMessage })
        Alert.Error(`Something went wrong, please try again. Details: ${errorMessage}`)
        return false
      }

      trackEvent('Booking Modify Success', { bookingId })
      Alert.Success('Booking modified with success!')
      this.updateBookingValuesFromServer()

      return result
    } catch (error) {
      Alert.Error(`An error occurred when submitting the booking alteration. D`)
      trackError('Error when submitting the booking alteration', {
        bookingId,
      })
    }
  }

  async acceptGuestAirbnbAlterationRequest(reservationAlterationId: string) {
    try {
      const result = await acceptGuestAirbnbAlteration(reservationAlterationId)

      if (result.warnings && result.warnings.length > 0) {
        trackError('Accepted Guest Airbnb Alteration Request Warning', {
          reservationAlterationId,
          warning: result.warnings[0].message,
        })
        Alert.Error(
          `Something went wrong, please try again. Details: ${result.warnings[0].message}`,
        )
        return false
      }

      if (result.errors && result.errors.length > 0) {
        const errorMessage = result.errors?.flatMap(error => error?.message).join('\n')
        trackError('Accepted Guest Airbnb Alteration Request Error', {
          reservationAlterationId,
          error: errorMessage,
        })
        Alert.Error(`Something went wrong, please try again. Details: ${errorMessage}`)
        return false
      }

      const alterationStatus = result.data.reservationAlterations.acceptGuestInitiatedAlteration
      if (alterationStatus === 'SYNCED') {
        trackEvent('Accepted Guest Airbnb Alteration Request', { reservationAlterationId })
        Alert.Success('Accepted guest alteration request with success!')
      } else if (alterationStatus === 'NO_RESPONSE') {
        trackEvent('Airbnb is still processing the alteration request. Please check again later.', {
          reservationAlterationId,
        })
        Alert.Info('Airbnb is still processing the alteration request. Please check again later.')
      }
      this.updateBookingValuesFromServer()
      return true
    } catch (error) {
      Alert.Error(`An error occurred when accepting the guest alteration`)
      trackError('Error when accepting the guest alteration', {
        reservationAlterationId,
      })
      return false
    }
  }

  async declineGuestAirbnbAlterationRequest(reservationAlterationId: string) {
    try {
      const result = await declineGuestAirbnbAlteration(reservationAlterationId)

      if (result.warnings && result.warnings.length > 0) {
        trackError('Declined Guest Airbnb Alteration Request Warning', {
          reservationAlterationId,
          warning: result.warnings[0].message,
        })
        Alert.Error(
          `Something went wrong, please try again. Details: ${result.warnings[0].message}`,
        )
        return false
      }

      if (result.errors && result.errors.length > 0) {
        const errorMessage = result.errors?.flatMap(error => error?.message).join('\n')
        trackError('Declined Guest Airbnb Alteration Request Error', {
          reservationAlterationId,
          error: errorMessage,
        })
        Alert.Error(`Something went wrong, please try again. Details: ${errorMessage}`)
        return false
      }

      const alterationStatus = result.data.reservationAlterations.declineGuestInitiatedAlteration
      if (alterationStatus === 'SYNCED') {
        trackEvent('Declined Guest Airbnb Alteration Request', { reservationAlterationId })
        Alert.Success('Declined guest alteration request with success!')
      } else if (alterationStatus === 'NO_RESPONSE') {
        trackEvent('Airbnb is still processing the alteration request. Please check again later.', {
          reservationAlterationId,
        })
        Alert.Info('Airbnb is still processing the alteration request. Please check again later.')
      }
      this.updateBookingValuesFromServer()
      return true
    } catch (error) {
      Alert.Error(`An error occurred when declining the guest alteration`)
      trackError('Error when declining the guest alteration', {
        reservationAlterationId,
      })
      return false
    }
  }

  async cancelHostAirbnbAlterationRequest(reservationAlterationId: string) {
    try {
      const result = await cancelHostAirbnbAlteration(reservationAlterationId)

      if (result.warnings && result.warnings.length > 0) {
        trackError('Cancel Host Airbnb Alteration Request Warning', {
          reservationAlterationId,
          warning: result.warnings[0].message,
        })
        Alert.Error(
          `Something went wrong, please try again. Details: ${result.warnings[0].message}`,
        )
        return false
      }

      if (result.errors && result.errors.length > 0) {
        const errorMessage = result.errors?.flatMap(error => error?.message).join('\n')
        trackError('Cancel Host Airbnb Alteration Request Error', {
          reservationAlterationId,
          error: errorMessage,
        })
        Alert.Error(`Something went wrong, please try again. Details: ${errorMessage}`)
        return false
      }

      const alterationStatus = result.data.reservationAlterations.cancelHostInitiatedAlteration
      if (alterationStatus === 'SYNCED') {
        trackEvent('Cancel Host Airbnb Alteration Request', { reservationAlterationId })
        Alert.Success('Canceled host alteration request with success!')
      } else if (alterationStatus === 'NO_RESPONSE') {
        trackEvent('Airbnb is still processing the alteration request. Please check again later.', {
          reservationAlterationId,
        })
        Alert.Info('Airbnb is still processing the alteration request. Please check again later.')
      }
      this.updateBookingValuesFromServer()
      return true
    } catch (error) {
      Alert.Error(`An error occurred when canceling the host alteration`)
      trackError('Error when canceling the host alteration', {
        reservationAlterationId,
      })
      return false
    }
  }

  async alterBookingMainGuest(bookingId: string, newMainGuestId: string) {
    try {
      await arriere.bookings.alterBookingMainGuest(bookingId, newMainGuestId).then(
        response => {
          if (response) {
            Alert.Success('Main guest updated successfully!')
            trackEvent('Alter Booking Main Guest', { bookingId, newMainGuestId })
            this.updateBookingValuesFromServer()
          } else {
            Alert.Error('An error occurred when altering main guest.')
            trackError('Error when altering booking main guest', { bookingId, newMainGuestId })
          }
        },
        error => {
          Alert.Error(`Oops! Something went wrong: ${error}.`)
          trackError('Error when altering booking main guest', { bookingId, newMainGuestId })
        },
      )
    } catch (error) {
      Alert.Error(`Oops! We were not able to process your request.`)
      trackError('Error when altering booking main guest', { bookingId, newMainGuestId })
    }
  }
}

export const BookingStore = new _BookingStore()
